import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { cpSendOTP, cpVerifyOTP } from '@/lib/api/controlPlaneClient';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';
import { CheckCircle2, Loader2 } from 'lucide-react';

const OTP_COOLDOWN_SECONDS = 60;

interface StepPhoneProps {
  onNext: () => void;
  onSkip?: () => void; // Optional - hidden in LINE mode (mandatory OTP)
}

/**
 * Validates Thai mobile number format (06x, 07x, 08x, 09x - 10 digits).
 * Accepts 0-prefix and +66/66-prefix formats.
 */
function isValidThaiPhone(phone: string): boolean {
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
  // Normalize 66 prefix to 0
  const normalized = cleaned.startsWith('66') && cleaned.length === 11
    ? '0' + cleaned.slice(2)
    : cleaned;
  return /^0[6-9]\d{8}$/.test(normalized);
}

/** Mask phone for display: 081-XXX-5678 */
function maskPhone(phone: string): string {
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
  const normalized = cleaned.startsWith('66') && cleaned.length === 11
    ? '0' + cleaned.slice(2)
    : cleaned;
  if (normalized.length === 10) {
    return `${normalized.slice(0, 3)}-XXX-${normalized.slice(7)}`;
  }
  return phone;
}

export function StepPhone({ onNext, onSkip }: StepPhoneProps) {
  const {
    formData,
    updateFormData,
    phoneVerified,
    otpRefCode,
    otpSentAt,
    otpStep,
    setOTPSent,
    setPhoneVerified,
    resetOTP,
  } = useRegistrationStore();

  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  // Local UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Recover countdown from persisted otpSentAt on mount (Audit Fix I7)
  useEffect(() => {
    if (otpStep === 'pin' && otpSentAt) {
      const elapsed = Math.floor((Date.now() - otpSentAt) / 1000);
      const remaining = OTP_COOLDOWN_SECONDS - elapsed;
      if (remaining > 0) {
        setCountdown(remaining);
      }
    }
  }, []); // Only on mount

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // ALL hooks must be above this line — React Rules of Hooks
  // No early returns before hooks, or React error #300 ("Rendered fewer hooks than expected")

  const handleSendOTP = useCallback(async () => {
    setError(null);

    if (!formData.phoneNumber.trim()) {
      setError(t.errorPhoneRequired);
      return;
    }

    if (!isValidThaiPhone(formData.phoneNumber)) {
      setError(t.errorPhoneInvalid);
      return;
    }

    setLoading(true);
    try {
      const response = await cpSendOTP(formData.phoneNumber);
      setOTPSent(response.ref_code);
      setCountdown(OTP_COOLDOWN_SECONDS);
      setPin('');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.toLowerCase().includes('too many')) {
        setError(t.errorTooManyOtp);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [formData.phoneNumber, setOTPSent, t]);

  const handleVerifyOTP = useCallback(async () => {
    setError(null);

    if (!pin.trim()) {
      setError(t.errorOtpInvalid);
      return;
    }

    setLoading(true);
    try {
      await cpVerifyOTP(formData.phoneNumber, pin.trim());
      setPhoneVerified();
      onNext();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.toLowerCase().includes('expired')) {
        setError(t.errorOtpExpired);
      } else {
        setError(t.errorOtpInvalid);
      }
      setPin('');
    } finally {
      setLoading(false);
    }
  }, [pin, formData.phoneNumber, setPhoneVerified, onNext, t]);

  const handleChangeNumber = () => {
    resetOTP();
    setPin('');
    setError(null);
    setCountdown(0);
  };

  const handleResend = () => {
    handleSendOTP();
  };

  // If already verified (user navigated back then returned), show verified state
  if (phoneVerified) {
    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-semibold text-foreground md:text-center">
              {t.phoneVerified}
            </h2>
          </div>
          <p className="mt-2 text-muted-foreground md:text-center">
            {maskPhone(formData.phoneNumber)}
          </p>
        </div>
        <Button onClick={onNext} className="w-full h-12 rounded-md text-base">
          {t.next}
        </Button>
      </div>
    );
  }

  // Phase: Enter Phone Number
  if (otpStep === 'phone') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground md:text-center">
            {t.verifyYourPhone}
          </h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">{t.phoneNumber}</Label>
            <Input
              id="phoneNumber"
              type="tel"
              inputMode="numeric"
              value={formData.phoneNumber}
              onChange={(e) => updateFormData({ phoneNumber: e.target.value })}
              placeholder={t.phoneNumberPlaceholder}
              className="h-12 rounded-md !border-border !bg-background"
              autoComplete="tel"
              autoFocus
              disabled={loading}
            />
          </div>
        </div>

        {error && <p className="text-sm text-destructive text-center">{typeof error === 'string' ? error : String(error)}</p>}

        <div className="flex gap-3">
          {onSkip && (
            <Button onClick={onSkip} variant="outline" className="flex-1 h-12 rounded-md">
              {t.skip}
            </Button>
          )}
          <Button
            onClick={handleSendOTP}
            className="flex-1 h-12 rounded-md text-base"
            disabled={loading || !formData.phoneNumber.trim()}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t.sendingOtp}
              </>
            ) : (
              t.sendOtpCode
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Phase: Enter OTP PIN
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {t.enterVerificationCode}
        </h2>
        <p className="mt-2 text-muted-foreground md:text-center">
          {t.otpSentTo} {maskPhone(formData.phoneNumber)}
          {otpRefCode && (
            <span className="block text-xs mt-1">
              {t.otpRefCode}: {otpRefCode}
            </span>
          )}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="otpPin">{t.enterVerificationCode}</Label>
          <Input
            id="otpPin"
            type="text"
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]*"
            autoComplete="one-time-code"
            value={pin}
            onChange={(e) => {
              // Only allow digits
              const value = e.target.value.replace(/\D/g, '');
              setPin(value);
            }}
            placeholder="000000"
            autoFocus
            disabled={loading}
            className="text-center text-2xl tracking-[0.5em] font-mono h-12 rounded-md"
          />
        </div>
      </div>

      {error && <p className="text-sm text-destructive text-center">{typeof error === 'string' ? error : String(error)}</p>}

      <Button
        onClick={handleVerifyOTP}
        className="w-full h-12 rounded-md text-base"
        disabled={loading || pin.length < 4}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t.verifyingCode}
          </>
        ) : (
          t.verifyCode
        )}
      </Button>

      <div className="flex justify-between text-sm">
        <button
          type="button"
          onClick={handleResend}
          disabled={countdown > 0 || loading}
          className={`${
            countdown > 0 || loading
              ? 'text-muted-foreground cursor-not-allowed'
              : 'text-primary hover:underline cursor-pointer'
          }`}
        >
          {countdown > 0
            ? `${t.resendIn} ${countdown}s`
            : t.resendCode}
        </button>
        <button
          type="button"
          onClick={handleChangeNumber}
          disabled={loading}
          className="text-primary hover:underline cursor-pointer disabled:text-muted-foreground disabled:cursor-not-allowed"
        >
          {t.changeNumber}
        </button>
      </div>
    </div>
  );
}
